import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  Banner,
  Button,
  Checkbox,
  ChipToggle,
  ErrorState,
  PageHead,
  Skeleton,
  TextArea,
  TextField,
  focusFirstInvalid,
  useToast,
} from "@ui/react";
import { ChevronLeftIcon } from "@ui/react/icons";
import {
  createExtra,
  listExtras,
  listServices,
  updateExtra,
  type ExtraInput,
} from "../../lib/api/contractor";
import type { ContractorService } from "../../lib/types/models";
import { errorMessage } from "../../lib/errorMessage";

const DEFAULT_CURRENCY = "ZAR";

export function ExtraEditorScreen() {
  const toast = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const { extraId = "" } = useParams();
  const navigate = useNavigate();

  const isCreate = extraId === "new";

  const [loadState, setLoadState] = useState<{ loading: boolean; error: unknown }>({
    loading: true,
    error: null,
  });
  const [reloadKey, setReloadKey] = useState(0);
  const [services, setServices] = useState<ContractorService[]>([]);

  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [durationMinutes, setDurationMinutes] = useState("0");
  const [priceAmount, setPriceAmount] = useState("");
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  const [enabled, setEnabled] = useState(true);
  const [serviceIds, setServiceIds] = useState<string[]>([]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;
    setLoadState({ loading: true, error: null });

    // The picker must show service names, so the catalogue loads even in create
    // mode. Only the extras read can fail the screen; a missing service list
    // degrades the picker, it does not block the form.
    void Promise.allSettled([listServices(), isCreate ? Promise.resolve([]) : listExtras()]).then(
      ([servicesResult, extrasResult]) => {
        if (ignore) return;

        if (servicesResult.status === "fulfilled") setServices(servicesResult.value);

        if (!isCreate) {
          if (extrasResult.status === "rejected") {
            setLoadState({ loading: false, error: extrasResult.reason });
            return;
          }
          const extra = extrasResult.value.find((candidate) => candidate.extraId === extraId);
          if (!extra) {
            setLoadState({ loading: false, error: new Error("That add-on no longer exists.") });
            return;
          }
          setLabel(extra.label);
          setDescription(extra.description ?? "");
          setDurationMinutes(String(extra.durationMinutes));
          setPriceAmount(String(extra.priceAmount));
          setCurrency(extra.priceCurrency || DEFAULT_CURRENCY);
          setEnabled(extra.enabled);
          setServiceIds(extra.applicableServiceIds ?? []);
        }

        setLoadState({ loading: false, error: null });
      },
    );

    return () => {
      ignore = true;
    };
  }, [isCreate, extraId, reloadKey]);

  const validate = (): boolean => {
    const next: Record<string, string> = {};

    if (!label.trim()) next["label"] = "Give this add-on a name.";

    const minutes = Number(durationMinutes);
    if (!Number.isInteger(minutes) || minutes < 0) {
      next["duration"] = "Use a whole number of minutes, 0 or more.";
    }

    const price = priceAmount.trim() ? Number(priceAmount) : 0;
    if (!Number.isFinite(price) || price < 0) {
      next["price"] = "Enter an amount of R0 or more.";
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSave = async () => {
    if (!validate()) {
      focusFirstInvalid(formRef.current);
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      const price = priceAmount.trim() ? Number(priceAmount) : 0;
      const body: ExtraInput = {
        label: label.trim(),
        description: description.trim(),
        durationMinutes: Number(durationMinutes),
        priceAmount: price,
        priceCurrency: currency || DEFAULT_CURRENCY,
        // An empty list means "every service", which is what the customer app
        // reads it as — so clearing the picker is a real choice, not a gap.
        applicableServiceIds: serviceIds,
        enabled,
      };

      if (isCreate) {
        await createExtra(body);
      } else {
        await updateExtra(extraId, body);
      }
      toast.show(isCreate ? "Add-on created." : "Add-on saved.");
      navigate("/settings/extras");
    } catch (error) {
      setSaveError(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };


  return (
    <div className="stack stack--loose">
      <PageHead
        eyebrow="Booking setup"
        title={isCreate ? "New add-on" : "Edit add-on"}
        back={
          <Link className="back-link" to="/settings/extras">
            <ChevronLeftIcon />
            All add-ons
          </Link>
        }
      />

      {loadState.loading ? (
        <div role="status">
          <span className="sr-only">Loading this add-on</span>
          <Skeleton height="20rem" />
        </div>
      ) : loadState.error ? (
        <ErrorState message={errorMessage(loadState.error)} onRetry={() => setReloadKey((key) => key + 1)} />
      ) : (
        <>
          {saveError ? (
            <Banner tone="danger" title="We couldn't save this">
              {saveError}
            </Banner>
          ) : null}

          <form
            ref={formRef}
            className="form"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              void onSave();
            }}
          >
            <div className="card card--dense">
              <div className="stack">
                <TextField
                  label="Add-on name"
                  required
                  value={label}
                  error={errors["label"] ?? null}
                  hint="What the customer sees when they tick it — e.g. Extra bathroom."
                  onChange={(event) => setLabel(event.target.value)}
                />
                <TextArea
                  label="Description"
                  value={description}
                  hint="Optional. What it covers."
                  onChange={(event) => setDescription(event.target.value)}
                />
                <div className="field-grid">
                  <TextField
                    label="Extra price (R)"
                    type="number"
                    min={0}
                    step={10}
                    placeholder="0"
                    value={priceAmount}
                    error={errors["price"] ?? null}
                    hint="Added to the booking total."
                    onChange={(event) => setPriceAmount(event.target.value)}
                  />
                  <TextField
                    label="Extra time (minutes)"
                    type="number"
                    min={0}
                    step={5}
                    value={durationMinutes}
                    error={errors["duration"] ?? null}
                    hint="Added to the slot length. 0 if it takes no longer."
                    onChange={(event) => setDurationMinutes(event.target.value)}
                  />
                </div>
                <Checkbox
                  label="Customers can add this to a booking"
                  checked={enabled}
                  onChange={(event) => setEnabled(event.target.checked)}
                />
              </div>
            </div>

            <div className="card card--dense">
              <div className="stack">
                <h2 className="headline-sm">Applies to</h2>
                <p className="muted">
                  Pick the services this add-on can be attached to. Leave every one unpicked to
                  offer it on all of them.
                </p>
                {services.length === 0 ? (
                  <p className="dim">
                    You have no services yet, so this add-on will be offered on all of them.
                  </p>
                ) : (
                  <div className="chip-row" role="group" aria-label="Services this add-on applies to">
                    {services.map((service) => {
                      const picked = serviceIds.includes(service.serviceId);
                      return (
                        <ChipToggle
                          key={service.serviceId}
                          pressed={picked}
                          onToggle={() =>
                            setServiceIds((current) =>
                              picked
                                ? current.filter((id) => id !== service.serviceId)
                                : [...current, service.serviceId],
                            )
                          }
                        >
                          {service.name}
                        </ChipToggle>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="cluster">
              <Button type="submit" loading={saving}>
                {isCreate ? "Create add-on" : "Save changes"}
              </Button>
              <Link className="btn btn-secondary" to="/settings/extras">
                Cancel
              </Link>
            </div>
          </form>
        </>
      )}
    </div>
  );
}
